import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { IntlShape, MessageDescriptor } from 'react-intl-next';

import Link from '@atlaskit/link';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { eventHub } from '@atlassian/editor-plugin-ai/EventHub';

import { useEditorAIReplacingTitle } from '@confluence/editor-features/entry-points/useEditorAIReplacingTitle.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useGeneratedTitleActions } from '@confluence/title-area-utils/entry-points/useGeneratedTitle.ts';

import { useDocumentPasteDetection } from './useDocumentPasteDetection.tsx';

export const TITLE_GENERATION_COMPLETE_TIMEOUT = 500;
export const AI_RESULT_VIEW_TIMEOUT = 1800000; // 30 minutes
export const AI_RESULT_VIEW_WORD_CHANGE_THRESHOLD = 50; // % word change before firing "deleted" event

// Word-based Levenshtein distance
const wordLevenshteinDistance = (str1: string, str2: string): number => {
	const words1 = str1.trim().split(/\s+/).filter(Boolean);
	const words2 = str2.trim().split(/\s+/).filter(Boolean);

	const m = words1.length;
	const n = words2.length;

	const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

	for (let i = 0; i <= m; i++) {
		dp[i][0] = i;
	}
	for (let j = 0; j <= n; j++) {
		dp[0][j] = j;
	}

	for (let i = 1; i <= m; i++) {
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		for (let j = 1; j <= n; j++) {
			if (words1[i - 1].toLowerCase() === words2[j - 1].toLowerCase()) {
				dp[i][j] = dp[i - 1][j - 1];
			} else {
				dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
			}
		}
	}

	return dp[m][n];
};

// Returns word change percentage (0-100) using Levenshtein distance normalized by max word count
export const getWordBasedTitleChangePercentage = (
	aiGeneratedTitle: string,
	currentTitle: string,
): number => {
	const aiWords = aiGeneratedTitle.trim().split(/\s+/).filter(Boolean);
	const currentWords = currentTitle.trim().split(/\s+/).filter(Boolean);
	const maxWordCount = Math.max(aiWords.length, currentWords.length);

	if (maxWordCount === 0) {
		return 100; // Both titles empty = 100% changed
	}
	const distance = wordLevenshteinDistance(aiGeneratedTitle, currentTitle);
	return (distance / maxWordCount) * 100;
};

type TitleGenerationI18n = {
	aiGeneratedTitleFromPaste: MessageDescriptor;
	aiGeneratedTitleFromPasteDescription: MessageDescriptor;
	aiGeneratedTitleDismissButton: MessageDescriptor;
};

export type UseTitleGenerationOnPasteProps = {
	textAreaRef: React.RefObject<HTMLTextAreaElement>;
	contentId: string;
	isLivePage: boolean;
	title?: string;
	isEditorEmpty?: boolean;
	userOffline?: boolean;
	i18n: TitleGenerationI18n;
	formatMessage: IntlShape['formatMessage'];
	flags?: FlagsStateContainer;
	onTitleGenerated?: (title: string) => void;
	onTitleGeneratingChange?: (isGenerating: boolean) => void;
};

const getTargetEditorId = (
	textAreaRef: React.RefObject<HTMLTextAreaElement>,
): string | undefined => {
	const editors = Array.from(
		textAreaRef.current
			?.closest('.ak-editor-content-area')
			?.querySelectorAll<HTMLElement>('.ProseMirror') ?? [],
	).filter((editor) => !editor.closest('[data-editor-popup]'));

	return editors[0]?.dataset.editorId;
};

export const useTitleGenerationOnPaste = ({
	textAreaRef,
	isEditorEmpty,
	userOffline,
	contentId,
	isLivePage,
	title,
	flags,
	i18n,
	formatMessage,
	onTitleGenerated,
	onTitleGeneratingChange,
}: UseTitleGenerationOnPasteProps) => {
	const [isGenerating, setIsGenerating] = useState(false);
	const generationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const aiResultViewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const wasGeneratingRef = useRef(false);
	const originalGeneratedTitleRef = useRef<string | null>(null);
	const generatedOnContentIdRef = useRef<string | null>(null);
	const currentTitleRef = useRef(title);
	const latestStreamedTitleRef = useRef<string>('');
	const fireResultAnalyticsRef = useRef<() => void>(() => { });
	const previousContentIdRef = useRef(contentId);

	currentTitleRef.current = title;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { trackAIInteractionInit, trackAIResultView, trackAIResultAction } =
		useAIEventsInstrumentation();

	const generatedTitleActions = useGeneratedTitleActions();

	const { pasteDetected, resetPasteDetected } = useDocumentPasteDetection(
		contentId,
		userOffline ?? false,
		isEditorEmpty ?? false,
		textAreaRef,
	);

	useEffect(() => {
		return () => {
			if (generationTimeoutRef.current) {
				clearTimeout(generationTimeoutRef.current);
			}

			if (aiResultViewTimeoutRef.current) {
				clearTimeout(aiResultViewTimeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!pasteDetected) {
			return;
		}

		const targetEditorId = getTargetEditorId(textAreaRef);

		if (targetEditorId) {
			trackAIInteractionInit({
				attributes: {
					aiFeatureName: 'aiTitleGenOnPaste',
					doesNotMeetMAUCriteria: true,
					isLivePage,
				},
			});
			eventHub.publishToEditor({
				event: 'start prompt',
				data: {
					targetEditorId,
					adjustSelection: 'fullDocument',
					analyticSourceId: 'confluenceTitleGenOnPaste',
					prompt: 'suggest title',
				},
			});
		}

		resetPasteDetected();
	}, [resetPasteDetected, pasteDetected, textAreaRef, trackAIInteractionInit, isLivePage]);

	useEffect(() => {
		if (flags && isGenerating) {
			void flags.hideFlag('ai-generated-title-from-paste-flag');
			void flags.showInfoFlag({
				id: 'ai-generated-title-from-paste-flag',
				title: formatMessage(i18n.aiGeneratedTitleFromPaste),
				description: formatMessage(i18n.aiGeneratedTitleFromPasteDescription, {
					Link: (chunks: React.ReactNode[]) => (
						<Link target="_blank" href="https://www.atlassian.com/trust/atlassian-intelligence">
							{chunks}
						</Link>
					),
				}),
				actions: [
					{
						content: formatMessage(i18n.aiGeneratedTitleDismissButton),
						onClick: () => flags.hideFlag('ai-generated-title-from-paste-flag'),
					},
				],
			});
		}
	}, [isGenerating, formatMessage, flags, i18n]);

	const fireResultAnalytics = useCallback(() => {
		const originalTitle = originalGeneratedTitleRef.current;
		const generatedOnContentId = generatedOnContentIdRef.current;
		if (originalTitle === null || generatedOnContentId === null) {
			return;
		}

		const currentTitle = currentTitleRef.current ?? '';

		if (originalTitle.trim().length > 0) {
			const changePercentage = getWordBasedTitleChangePercentage(originalTitle, currentTitle);

			if (changePercentage < AI_RESULT_VIEW_WORD_CHANGE_THRESHOLD) {
				trackAIResultView({
					attributes: {
						aiFeatureName: 'aiTitleGenOnPaste',
						doesNotMeetMAUCriteria: true,
						isLivePage,
					},
				});
				trackAIResultAction('aiTitleGenOnPaste', {
					attributes: {
						aiFeatureName: 'aiTitleGenOnPaste',
						doesNotMeetMAUCriteria: true,
						isLivePage,
					},
				});
			} else {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'deleted',
						actionSubject: 'aiTitleGenOnPaste',
						source: 'confluencePage',
						attributes: {
							contentId: generatedOnContentId,
							isLivePage,
						},
					},
				}).fire();
			}
		}

		originalGeneratedTitleRef.current = null;
		generatedOnContentIdRef.current = null;
		if (aiResultViewTimeoutRef.current) {
			clearTimeout(aiResultViewTimeoutRef.current);
			aiResultViewTimeoutRef.current = null;
		}

		// Clear stored callback so publish doesn't fire analytics again (standard pages only)
		if (!isLivePage) {
			generatedTitleActions.clearGeneratedTitleFromPaste();
		}
	}, [
		isLivePage,
		createAnalyticsEvent,
		trackAIResultView,
		trackAIResultAction,
		generatedTitleActions,
	]);

	useEffect(() => {
		if (wasGeneratingRef.current && !isGenerating) {
			const generatedTitle = latestStreamedTitleRef.current ?? '';
			originalGeneratedTitleRef.current = generatedTitle;
			generatedOnContentIdRef.current = contentId;

			if (generatedTitle && onTitleGenerated) {
				onTitleGenerated(generatedTitle);
			}

			if (aiResultViewTimeoutRef.current) {
				clearTimeout(aiResultViewTimeoutRef.current);
			}

			aiResultViewTimeoutRef.current = setTimeout(() => {
				fireResultAnalytics();
			}, AI_RESULT_VIEW_TIMEOUT);

			// Standard pages: also store callback for publish trigger
			if (!isLivePage) {
				generatedTitleActions.setGeneratedTitleFromPaste(generatedTitle);
				generatedTitleActions.setFireResultAnalyticsFromPaste(fireResultAnalytics);
			}
		}

		wasGeneratingRef.current = isGenerating;

		onTitleGeneratingChange?.(isGenerating);
	}, [
		isGenerating,
		fireResultAnalytics,
		contentId,
		onTitleGenerated,
		onTitleGeneratingChange,
		isLivePage,
		generatedTitleActions,
	]);

	fireResultAnalyticsRef.current = fireResultAnalytics;

	// Fire analytics when contentId changes (page navigation where component doesn't unmount)
	// This handles scenarios like live page -> live page navigation
	useEffect(() => {
		if (previousContentIdRef.current !== contentId) {
			fireResultAnalyticsRef.current();
			previousContentIdRef.current = contentId;
		}
	}, [contentId]);

	// Fire analytics on page leave/unmount
	useEffect(() => {
		const handleBeforeUnload = () => {
			fireResultAnalyticsRef.current();
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			fireResultAnalyticsRef.current();
		};
	}, []);

	// Track streaming chunks to detect when generation completes
	const handleTitleChunk = useCallback((newTitle: string) => {
		latestStreamedTitleRef.current = newTitle;

		if (generationTimeoutRef.current) {
			clearTimeout(generationTimeoutRef.current);
		}
		setIsGenerating(true);

		generationTimeoutRef.current = setTimeout(() => {
			setIsGenerating(false);
			generationTimeoutRef.current = null;
		}, TITLE_GENERATION_COMPLETE_TIMEOUT);
	}, []);

	useEditorAIReplacingTitle({
		enabled: true,
		title: title || '',
		handleNewTitle: handleTitleChunk,
		canFireUnifiedAIAnalyticsEvent: false,
	});
};
